import React from 'react';
import NProgress from 'nprogress';
import { useRouter } from 'next/router';
import { useMutation } from '@apollo/client';
import { Alert, DocumentForm } from '../shared';
import { useNotify } from '../../hooks';
import {
  Address,
  Company,
  CompanyStatusEnum,
  DocumentModel,
  User,
} from '../../models';
import { CREATE_USER, UPDATE_USER } from '../../graphql/mutation';
import { roles } from '../../static';
import { validateUserForm } from '../../lib/formValidations/validateUserForm';
import {
  validateAddressForm,
  validateCompanyForm,
} from '../../lib/formValidations';
import {
  UserFormFields,
  reducer as userReducer,
} from '../shared/Forms/GeneralUserFormFields';
import {
  AddressForm,
  reducer as addressReducer,
} from '../shared/Forms/AddressForm';
import {
  CompanyFormFields,
  reducer as companyReducer,
} from '../shared/Forms/CompanyFormFields';
import SearchCompany from '../shared/Forms/CompanyFormFields/SearchCompany';
import FormButtons from '../shared/Forms/FormButtons';
import { companyStatus } from '../../static/companyStatus';
import { removeProperties } from '../../lib/removeProperties';

interface AllyFormProps {
  ally?: User;
}

function AllyForm({ ally }: AllyFormProps) {
  const notify = useNotify();
  const router = useRouter();
  const [disabled, setDisabled] = React.useState(false);
  const [addressReadOnly, setAddressReadOnly] = React.useState(false);
  const [companyReadOnly, setCompanyReadOnly] = React.useState(false);

  const [createAllyMutation] = useMutation<{
    createUser: User;
  }>(CREATE_USER);

  const [updateAllyMutation] = useMutation<{
    updateUser: User;
  }>(UPDATE_USER);

  const { company } = ally || {};
  const { addresses } = (company as Company) || {};

  const [userState, userDispatch] = React.useReducer(userReducer, ally ?? {});

  const [allyState, allyDispatch] = React.useReducer(
    companyReducer,
    ally ? { ...(company as Company) } : {}
  );

  const [addressState, addressDispatch] = React.useReducer(
    addressReducer,
    ally ? { ...addresses[0] } : {}
  );

  const [documents, setDocuments] = React.useState<
    Array<Partial<DocumentModel>>
  >(
    (company as Company)?.logo?.src
      ? [{ src: (company as Company)?.logo?.src }]
      : []
  );

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    try {
      e.preventDefault();
      NProgress.start();
      // disabled validation
      if (disabled) return;
      setDisabled(true);
      // validate form
      const userError = validateUserForm({
        user: { ...userState },
        update: !!ally?._id,
        requiredDni: false,
      });
      if (userError) return notify(userError, 'error');
      const allyError = validateCompanyForm({
        ...allyState,
      });

      if (allyError) return notify(allyError, 'error');

      const addressError = validateAddressForm({ ...addressState });
      if (addressError) return notify(addressError, 'error');
      // build object
      const allyData = {
        ...userState,
        userType: roles.ally,
        dniType: allyState.dniType ?? 'J',
        dni: allyState.dni,
        company: {
          ...allyState,
          dniType: allyState.dniType ?? 'J',
          status: (ally?.company as Company)?.status ?? 'pending',
          logo: { src: (documents[0]?.src as string) ?? null },
        } as Company,
      };

      // create or update user
      if (ally) {
        delete allyData.company.shopCart;
        await updateAlly({
          ...removeProperties(allyData),
          company: {
            ...removeProperties(allyData.company),
            addresses: [
              {
                ...addressState,
                primary: true,
                userId: ally?._id ?? undefined,
                __typename: undefined,
              } as Address,
            ],
          },
        } as Partial<User>);
      } else {
        await createAlly(allyData);
      }
    } catch (error) {
      // error duplicate email
      if (error.message.includes('dup key: { email')) {
        return notify('El correo ya se encuentra registrado', 'error');
      }
      // error duplicate dni
      if (error.message.includes('dup key: { dni')) {
        return notify('El DNI ya se encuentra registrado', 'error');
      }
      notify(error.message, 'error');
    } finally {
      NProgress.done();
      setDisabled(false);
    }
  };

  const createAlly = async (allyItem: Partial<User>) => {
    const allyData = removeProperties(allyItem);
    delete (allyData?.company as Company)?._id;
    delete (allyData?.company as Company)?.active;
    delete (allyData?.company as Company)?.shopCart;
    delete (allyData?.company as Company)?.addresses;
    delete (allyData?.company as Company)?.companyDetails;

    const { data } = await createAllyMutation({
      variables: {
        data: {
          ...allyData,
          companyId: allyState?._id ?? undefined,
          __typename: undefined,
          company: {
            ...(allyData.company as Company),
            __typename: undefined,
          },
          addresses: [
            {
              ...addressState,
              ...(ally?._id ? { userId: ally?._id } : {}),
              primary: true,
              __typename: undefined,
              _id: undefined,
            } as Address,
          ],
        },
      },
    });
    if (data?.createUser?._id) {
      notify('Aliado se ha creado con éxito', 'success');
      router.back();
    } else {
      notify('Ha ocurrido un error al crear el aliado', 'error');
    }
  };

  const updateAlly = async (allyItem: Partial<User>) => {
    const allyData = removeProperties(allyItem);
    delete allyData?.firstLogin;
    delete allyData?.discountCoupons;
    delete allyData?.currentSubscription;
    delete (allyData?.company as Company)?.companyDetails;

    const { data } = await updateAllyMutation({
      variables: {
        data: {
          ...allyData,
          _id: ally?._id,
          client: undefined,
          ...(allyData?.company
            ? {
                company: {
                  ...(allyData.company as Company),
                  __typename: undefined,
                },
              }
            : {}),
          __typename: undefined,
        },
      },
    });
    if (data?.updateUser) {
      notify('Aliado actualizado con éxito', 'success');
      router.back();
    } else {
      notify('Ha ocurrido un error al actualizar el aliado', 'error');
    }
  };

  const changeAllyActive = async () => {
    await updateAlly({
      active: !ally.active,
    });
  };

  const changeAllyStatus = async (status: CompanyStatusEnum) => {
    const data = {
      company: {
        _id: allyState._id,
        status,
        addresses: [
          {
            ...addressState,
            userId: ally._id,
            primary: true,
            __typename: undefined,
          } as Address,
        ],
      },
    } as Partial<User>;

    await updateAlly(data);
  };

  return (
    <form
      method="POST"
      onSubmit={onSubmit}
      className="flex flex-col w-full space-y-10"
    >
      <Alert
        title="Debe aprobar el nuevo aliado"
        text="Para poder realizar acciones con este aliado, debe aprobarlo primero."
        show={(ally?.company as Company)?.status === companyStatus.pending}
      />
      <Alert
        title="Este aliado ha sido rechazado"
        text="Este aliado ha sido rechazado, por tanto no podrá realizar acciones en la plataforma."
        show={(ally?.company as Company)?.status === companyStatus.rejected}
      />
      {!ally ? (
        <SearchCompany
          header={{
            title: '¿Tu compañia ya esta registrada en Meit?',
          }}
          companyDispatch={allyDispatch}
          addressDispatch={addressDispatch}
          setCompanyReadOnly={setCompanyReadOnly}
          setAddressReadOnly={setAddressReadOnly}
        />
      ) : null}
      <CompanyFormFields
        dispatch={allyDispatch}
        state={allyState}
        userType={roles.ally}
        header={{
          title: 'Información del aliado',
          href: '/app/allies',
          backBtnText: 'Lista de aliados',
        }}
        readOnly={companyReadOnly}
      />
      <UserFormFields
        dispatch={userDispatch}
        state={userState}
        userType={roles.ally}
        header={{
          title: 'Información del usuario',
        }}
        requiredUserDni={false}
      />
      <AddressForm
        dispatch={addressDispatch}
        state={addressState}
        readOnly={addressReadOnly}
        header={{
          title: 'Dirreción del aliado',
        }}
      />
      <DocumentForm
        documents={documents}
        updateURLs={setDocuments}
        maximumAmount={1}
      />
      <FormButtons
        type={ally ? 'update' : 'create'}
        toggleActive={changeAllyActive}
        toggleStatus={changeAllyStatus}
        active={ally?.active}
        status={(ally?.company as Company)?.status}
        disabled={disabled}
        onSubmit={onSubmit}
      />
    </form>
  );
}

export default AllyForm;
